import React, { useState, useEffect, useCallback } from 'react';
import { Box, Snackbar, Alert, Tabs, Tab } from '@mui/material';
import AdminSidebar from './AdminSidebar';
import AdminProfile from './AdminProfile';
import RoleManagement from './RoleManagement';
import MunicipalitiesManagement from './MunicipalitiesManagement';
import AdminDialog from './AdminDialog';
import ChangePasswordDialog from './ChangePasswordDialog';
import { Admin, Role, Municipality } from './type';
import { Province } from '../conf/type';
import { COLORS_ADMIN_PAGE } from '../conf/constants';
import { ADMIN_MANAGEMENT_CONSTANTS } from './constants/adminManagement';
import { useErrorHandler } from '../utils/useErrorHandler';
import { useAdminContext } from '../utils/AdminContext';
import dataProvider from '../services/api';

const AdministratorsManagement: React.FC = () => {
  const { snackbar, handleErrorWithLogout, showSuccessMessage, showErrorMessage, handleSnackbarClose } = useErrorHandler();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const { setAvatarUrl } = useAdminContext();
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState<number>(ADMIN_MANAGEMENT_CONSTANTS.DEFAULT_USERS_PER_PAGE);
  const fastPageJump = ADMIN_MANAGEMENT_CONSTANTS.DEFAULT_FAST_PAGE_JUMP;

  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [assignedRoles, setAssignedRoles] = useState<Role[]>([]);
  const [rolesManuallyChanged, setRolesManuallyChanged] = useState(false);

  const [assignedMunicipalities, setAssignedMunicipalities] = useState<Municipality[]>([]);
  const [municipalitiesChanged, setMunicipalitiesChanged] = useState(false);
  const [provinceSearch, setProvinceSearch] = useState<Province | null>(null);

  const [currentTab, setCurrentTab] = useState(0);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [totalAdmins, setTotalAdmins] = useState(0);

  const handleUsersPerPageChange = useCallback((newUsersPerPage: number) => {
    if (newUsersPerPage !== usersPerPage) {
      setUsersPerPage(newUsersPerPage);
      setCurrentPage(1); 
    }
  }, [usersPerPage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([fetchAdmins(), fetchAllRoles(), fetchAdminCount()]);
      } catch (error: any) {
        handleErrorWithLogout(error);
        setError(null);
      }
    };
    fetchData();
  }, [currentPage, searchQuery, usersPerPage]);

  useEffect(() => {
    if (selectedAdmin) {
      setFormData({
        username: selectedAdmin.username,
        email: selectedAdmin.email,
        firstName: selectedAdmin.firstName,
        middleName: selectedAdmin.middleName || '',
        lastName: selectedAdmin.lastName,
        phoneNumber: selectedAdmin.phoneNumber || '',
        password: '',
      });
      if (!rolesManuallyChanged) {
        fetchAssignedRoles();
      }
      if (!municipalitiesChanged) {
        fetchAssignedMunicipalities();
      }
    } else {
      setFormData({
        username: '',
        email: '',
        firstName: '',
        middleName: '',
        lastName: '',
        phoneNumber: '',
        password: '',
      });
      setAvailableRoles([...allRoles]);
      setAssignedRoles([]);
      setAssignedMunicipalities([]);
      setProvinceSearch(null);
      setRolesManuallyChanged(false);
      setMunicipalitiesChanged(false);
    }
  }, [selectedAdmin, allRoles, rolesManuallyChanged, municipalitiesChanged]);

  const fetchAdmins = async () => {
    try {
      const { data } = await dataProvider.getList('administration', {
        pagination: { page: currentPage - 1, perPage: usersPerPage },
        sort: { field: 'administratorId', order: 'ASC' },
        filter: { filter: searchQuery },
      });
      setAdmins(data);
      setError(null);
    } catch (error: any) {
      setAdmins([]);
      handleErrorWithLogout(error);
    }
  };

  const fetchAdminCount = async () => {
    try {
      const { data } = await dataProvider.getAdminCount({
        filter: searchQuery,
        count: usersPerPage,
      });
      setTotalAdmins(data.count);
    } catch (error: any) {
      handleErrorWithLogout(error);
      setTotalAdmins(0);
    }
  };

  const fetchAllRoles = async () => {
    try {
      const { data } = await dataProvider.getAllRoles('administration');
      setAllRoles(data);
      if (!selectedAdmin) {
        setAvailableRoles(data);
      } else if (!rolesManuallyChanged) {
        await fetchAssignedRoles();
      }
    } catch (error: any) {
      setAllRoles([]);
      setAvailableRoles([]);
      handleErrorWithLogout(error);
    }
  };

  const fetchAssignedRoles = async () => {
    if (!selectedAdmin) return;
    try {
      const { data } = await dataProvider.getAssignedRoles({ id: selectedAdmin.administratorId });
      setAssignedRoles(data);
      const assignedRoleIds = new Set(data.map((role: Role) => role.id.toString()));
      setAvailableRoles(allRoles.filter((role) => !assignedRoleIds.has(role.id.toString())));
      setRolesManuallyChanged(false);
    } catch (error: any) {
      setAssignedRoles([]);
      setAvailableRoles([...allRoles]);
      handleErrorWithLogout(error);
    }
  };

  const fetchAssignedMunicipalities = async () => {
    if (!selectedAdmin) return;
    try {
      const { data } = await dataProvider.getAssignedMunicipalities({ id: selectedAdmin.administratorId });
      setAssignedMunicipalities(data);
      setMunicipalitiesChanged(false);
    } catch (error: any) {
      setAssignedMunicipalities([]);
      handleErrorWithLogout(error);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async () => {
    try {
      if (selectedAdmin) {
        const { data: updatedAdmin } = await dataProvider.update('administration', {
          id: selectedAdmin.administratorId,
          data: formData,
          previousData: selectedAdmin,
        });

        await dataProvider.assignRoles({
          id: selectedAdmin.administratorId,
          roleIds: assignedRoles.map((role) => role.id),
        });

        if (municipalitiesChanged) {
          await dataProvider.assignMunicipalities({
            id: selectedAdmin.administratorId,
            municipalityIds: assignedMunicipalities.map((municipality) => municipality.id),
          });
        }

        await Promise.all([fetchAdmins(), fetchAssignedRoles(), fetchAssignedMunicipalities()]);
        setSelectedAdmin({ ...selectedAdmin, ...updatedAdmin });
        setRolesManuallyChanged(false);
        setMunicipalitiesChanged(false);
        showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.ADMIN_UPDATED_SUCCESS);
      } else {
        const { data } = await dataProvider.create('administration', { data: formData });
        await fetchAdmins();
        setIsFormOpen(false);
        showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.ADMIN_CREATED_SUCCESS);
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setPasswordError(null);
  };

  const handlePasswordSubmit = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.PASSWORD_MISMATCH_ERROR);
      return;
    }
    try {
      await dataProvider.customResetPassword({
        id: selectedAdmin?.administratorId || 0,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.PASSWORD_CHANGED_SUCCESS);
      setIsChangePasswordOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleDelete = async (adminId: number) => {
    if (window.confirm(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.CONFIRM_DELETE)) {
      try {
        await dataProvider.delete('administration', { id: adminId });
        await fetchAdmins();
        showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.ADMIN_DELETED_SUCCESS);
        setSelectedAdmin(null);
        setFormData({
          username: '',
          email: '',
          firstName: '',
          middleName: '',
          lastName: '',
          phoneNumber: '',
          password: '',
        });
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  const handleCreate = () => {
    setSelectedAdmin(null);
    setFormData({
      username: '',
      email: '',
      firstName: '',
      middleName: '',
      lastName: '',
      phoneNumber: '',
      password: '',
    });
    setIsFormOpen(true);
  };

  const handleUploadAvatar = async () => {
    if (!selectedAdmin && !isAuthenticatedAdmin()) {
      showErrorMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.SELECT_ADMIN_FIRST);
      return;
    }

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        try {
          let response;
          if (!selectedAdmin || isCurrentAdmin(selectedAdmin.administratorId)) {
            response = await dataProvider.uploadProfileImage('administration', { file });
          } else {
            response = await dataProvider.manageUploadProfileImage({
              administratorId: selectedAdmin.administratorId,
              file,
            });
          }

          const { data } = await dataProvider.getList('administration', {
            pagination: { page: currentPage - 1, perPage: usersPerPage },
            sort: { field: 'administratorId', order: 'ASC' },
            filter: { filter: searchQuery },
          });
          setAdmins(data);
          const updatedAdmin = data.find((admin: Admin) => admin.administratorId === (selectedAdmin?.administratorId || getCurrentAdminId()));
          if (updatedAdmin) {
            setSelectedAdmin(updatedAdmin);
            if (!selectedAdmin || selectedAdmin.administratorId === getCurrentAdminId()) {
              setAvatarUrl(updatedAdmin.profileImageUrl || null);
            }
            showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.AVATAR_UPLOADED_SUCCESS);
          }
        } catch (error: any) {
          handleErrorWithLogout(error);
        }
      }
    };
    fileInput.click();
  };

  const isAuthenticatedAdmin = () => {
    return !!localStorage.getItem('sessionToken');
  };

  const getCurrentAdminId = () => {
    const currentAdminId = localStorage.getItem('currentAdminId');
    return currentAdminId ? parseInt(currentAdminId) : -1;
  };

  const isCurrentAdmin = (adminId: number) => {
    return adminId === getCurrentAdminId();
  };

  const handleChangePassword = () => {
    setIsChangePasswordOpen(true);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError(null);
  };

  const moveRoleToAssigned = (...roles: Role[]) => {
    const roleIds = new Set(roles.map((role) => role.id));
    setAvailableRoles((prev) => [...prev.filter((role) => !roleIds.has(role.id))]);
    setAssignedRoles((prev) => [...prev, ...roles]);
    setRolesManuallyChanged(true);
  };

  const moveRoleToAvailable = (...roles: Role[]) => {
    const roleIds = new Set(roles.map((role) => role.id));
    setAssignedRoles((prev) => [...prev.filter((role) => !roleIds.has(role.id))]);
    setAvailableRoles((prev) => [...prev, ...roles]);
    setRolesManuallyChanged(true);
  };

  const onDragEnd = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === destination.droppableId) return;

    if (source.droppableId === 'availableRoles' && destination.droppableId === 'assignedRoles') {
      const roleToMove = availableRoles[source.index];
      if (roleToMove) {
        moveRoleToAssigned(roleToMove);
      }
    } else if (source.droppableId === 'assignedRoles' && destination.droppableId === 'availableRoles') {
      const roleToMove = assignedRoles[source.index];
      if (roleToMove) {
        moveRoleToAvailable(roleToMove);
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleAssignAllMunicipalities = async (page: number = 0, count: number = 100) => {
    if (!selectedAdmin) {
      showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.SELECT_ADMIN_FIRST);
      return;
    }

    try {
      const { data: allMunicipalities } = await dataProvider.getAllMunicipalities('administration', { page, count });
      const allMunicipalityIds = allMunicipalities.map((m: Municipality) => m.id);
      const currentMunicipalityIds = assignedMunicipalities.map((m) => m.id);
      const updatedMunicipalityIds = [...currentMunicipalityIds, ...allMunicipalityIds];

      await dataProvider.assignMunicipalities({
        id: selectedAdmin.administratorId,
        municipalityIds: updatedMunicipalityIds,
      });

      await fetchAssignedMunicipalities();
      showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.ALL_MUNICIPALITIES_ASSIGNED_SUCCESS);
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleRemoveAssignments = async () => {
    if (!selectedAdmin) {
      showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.SELECT_ADMIN_FIRST);
      return;
    }

    try {
      await dataProvider.assignMunicipalities({
        id: selectedAdmin.administratorId,
        municipalityIds: [],
      });
      await fetchAssignedMunicipalities();
      showSuccessMessage(ADMIN_MANAGEMENT_CONSTANTS.MESSAGES.ALL_MUNICIPALITIES_REMOVED_SUCCESS);
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  return (
    <Box sx={{ display: 'flex', p: 2, backgroundColor: COLORS_ADMIN_PAGE.BACKGROUND}}>
      <AdminSidebar
        admins={admins}
        selectedAdmin={selectedAdmin}
        setSelectedAdmin={setSelectedAdmin}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        error={error}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        usersPerPage={usersPerPage}
        fastPageJump={fastPageJump}
        totalAdmins={totalAdmins}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
        onUsersPerPageChange={handleUsersPerPageChange} 
      />
      <Box sx={{ flexGrow: 1, p: 2, bgcolor: COLORS_ADMIN_PAGE.CARD_BACKGROUND, borderRadius: 2, boxShadow: 1 }}>
        <AdminProfile
          selectedAdmin={selectedAdmin}
          formData={formData}
          handleFormChange={handleFormChange}
          handleFormSubmit={handleFormSubmit}
          handleUploadAvatar={handleUploadAvatar}
          handleChangePassword={handleChangePassword}
          assignedRoles={assignedRoles}
        />
        {selectedAdmin && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                aria-label="admin management tabs"
                sx={{
                  '& .MuiTabs-indicator': {
                    backgroundColor: COLORS_ADMIN_PAGE.PRIMARY,
                  },
                }}
              >
                <Tab
                  label="Roles"
                  id="roles-tab"
                  aria-controls="roles-panel"
                  sx={{
                    '&.Mui-selected': {
                      color: COLORS_ADMIN_PAGE.PRIMARY,
                    },
                  }}
                />
                <Tab
                  label="Municipalities"
                  id="municipalities-tab"
                  aria-controls="municipalities-panel"
                  sx={{
                    '&.Mui-selected': {
                      color: COLORS_ADMIN_PAGE.PRIMARY,
                    },
                  }}
                />
              </Tabs>
            </Box>

            <Box role="tabpanel" hidden={currentTab !== 0} id="roles-panel" aria-labelledby="roles-tab">
              {currentTab === 0 && (
                <RoleManagement
                  availableRoles={availableRoles}
                  setAvailableRoles={setAvailableRoles}
                  assignedRoles={assignedRoles}
                  setAssignedRoles={setAssignedRoles}
                  onDragEnd={onDragEnd}
                  moveRoleToAssigned={moveRoleToAssigned}
                  moveRoleToAvailable={moveRoleToAvailable}
                />
              )}
            </Box>

            <Box role="tabpanel" hidden={currentTab !== 1} id="municipalities-panel" aria-labelledby="municipalities-tab">
              {currentTab === 1 && (
                <MunicipalitiesManagement
                  assignedMunicipalities={assignedMunicipalities}
                  setAssignedMunicipalities={setAssignedMunicipalities}
                  setMunicipalitiesChanged={setMunicipalitiesChanged}
                  handleAssignAllMunicipalities={handleAssignAllMunicipalities}
                  handleRemoveAssignments={handleRemoveAssignments}
                  administratorId={selectedAdmin.administratorId}
                  provinceSearch={provinceSearch}
                  setProvinceSearch={setProvinceSearch}
                />
              )}
            </Box>
          </>
        )}
      </Box>
      <AdminDialog
        isFormOpen={isFormOpen}
        setIsFormOpen={setIsFormOpen}
        selectedAdmin={selectedAdmin}
        formData={formData}
        handleFormChange={handleFormChange}
        handleFormSubmit={handleFormSubmit}
      />
      <ChangePasswordDialog
        isChangePasswordOpen={isChangePasswordOpen}
        setIsChangePasswordOpen={setIsChangePasswordOpen}
        passwordData={passwordData}
        handlePasswordChange={handlePasswordChange}
        handlePasswordSubmit={handlePasswordSubmit}
        passwordError={passwordError}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={ADMIN_MANAGEMENT_CONSTANTS.SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdministratorsManagement;